// src/components/auth/Auth0Callback.tsx
import React, { useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSnackbar } from '../snackbar/SnackbarProvider';
import pathnames from '../../routes/index.route';

export const Auth0Callback: React.FC = () => {
  const { isAuthenticated, user, error, getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showSnackbar } = useSnackbar();

  useEffect(() => {
    const handleCallback = async () => {
      // Log all URL parameters for debugging in a way that works with ES5
      const urlParams: Record<string, string> = {};
      searchParams.forEach((value, key) => {
        urlParams[key] = value;
      });
      console.log('URL Parameters:', urlParams);
      console.log('Auth State:', { isAuthenticated, user, error });

      const errorParam = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');
      const state = searchParams.get('state');
      const code = searchParams.get('code');

      // Log complete error information
      if (errorParam || error) {
        console.log('Full Error Details:', {
          errorParam,
          errorDescription,
          authError: error,
          state,
          code
        });

        if (errorParam === 'access_denied') {
          // Check if it's a token error
          try {
            await getAccessTokenSilently();
            // If we get here, we actually have a valid token
            if (isAuthenticated && user) {
              showSnackbar({
                message: 'Successfully authenticated!',
                type: 'success'
              });
              navigate(pathnames.home);
              return;
            }
          } catch (tokenError) {
            console.log('Token Error:', tokenError);
          }

          showSnackbar({
            message: 'Authentication was declined. Please try again.',
            type: 'warning'
          });
          navigate(pathnames.signIn);
          return;
        }

        showSnackbar({
          message: errorDescription || 'Authentication failed. Please try again.',
          type: 'error'
        });
        navigate(pathnames.signIn);
        return;
      }

      // Handle successful authentication
      if (isAuthenticated && user) {
        let returnTo = pathnames.home;

        try {
          if (state) {
            const decodedState = atob(state);
            console.log('Decoded State:', decodedState);
            const stateParams = JSON.parse(decodedState);
            returnTo = stateParams.returnTo || returnTo;
          }
        } catch (e) {
          console.error('Error parsing state:', e);
        }

        showSnackbar({
          message: 'Successfully logged in!',
          type: 'success'
        });
        navigate(returnTo);
      }
    };

    handleCallback();
  }, [isAuthenticated, navigate, searchParams, showSnackbar, user, error, getAccessTokenSilently]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="p-8 bg-white rounded-lg shadow-md max-w-md w-full">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            {error ? 'Authentication Error' : 'Completing Authentication'}
          </h2>
          {error ? (
            <>
              <p className="text-red-600 mb-4">
                {error.message || 'An error occurred during authentication'}
              </p>
              <pre className="text-left text-sm bg-gray-100 p-4 rounded mb-4 overflow-auto">
                {JSON.stringify(
                  Array.from(searchParams.entries()).reduce((acc, [key, value]) => {
                    acc[key] = value;
                    return acc;
                  }, {} as Record<string, string>),
                  null,
                  2
                )}
              </pre>
              <button
                onClick={() => navigate(pathnames.signIn)}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition"
              >
                Return to Login
              </button>
            </>
          ) : (
            <>
              <div className="flex justify-center mb-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              </div>
              <p className="text-gray-600">
                Please wait while we complete the process...
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};